import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClientSubscriptionDialog } from "@/components/clients/ClientSubscriptionDialog";
import { BlockClientDialog } from "@/components/clients/BlockClientDialog";
import { ClientInfoCard } from "@/components/clients/ClientInfoCard";
import { ClientDetailsTabs } from "@/components/clients/ClientDetailsTabs";
import { ExtendSubscriptionDialog } from "@/components/clients/ExtendSubscriptionDialog";
import { PauseSubscriptionDialog } from "@/components/clients/PauseSubscriptionDialog";
import { useClientsSupabaseStore } from "@/store/api/clientsSupabaseStore";
import { useTranslation } from "react-i18next";
import { isSubscriptionExpired } from "@/components/subscriptions/SubscriptionFormSchema";
import { Subscription } from "@/types/subscription";
import { Client } from "@/types/types";
import { notificationStore } from "@/store/api/notificationStore";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";


import { useToast } from "@/hooks/use-toast";

const ClientDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditSubscriptionOpen, setIsEditSubscriptionOpen] = useState(false);
  const [isExtendSubscriptionOpen, setIsExtendSubscriptionOpen] = useState(false);
  const [isPauseSubscriptionOpen, setIsPauseSubscriptionOpen] = useState(false);
  const [isBlockClientOpen, setIsBlockClientOpen] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | undefined>(undefined);
  const [paymentsBySub, setPaymentsBySub] = useState<Record<string, any[]>>({});

  const { t } = useTranslation(["common", "subscription", "adminui3"]);
  
  const clientsStore = useClientsSupabaseStore();

  const { toast } = useToast();

  const loadForUser = useClientSubscriptionStore(
    (s) => s.loadClientSubscriptionDataForUser
  );

  const client = clientsStore.selectedClient as unknown as Client | null;
  
  useEffect(() => {
    if (!id) return;
    clientsStore.fetchClientById(id);

    // charger paiements/abonnements pour CE client
    (async () => {
      try {
        const { paymentsBySub, allSubscriptions, subscription } = await loadForUser(id);
        setPaymentsBySub(paymentsBySub);
        //setAllSubsForUser(allSubscriptions);
        //setActiveSubForUser(subscription);
      } catch (e) {
        console.error("loadForUser error:", e);
      }
    })();
  }, [id]);
  
  
  const sendReminder = () => {
    void (async () => {
      const res = await notificationStore.createAdminMessage(id, {
        text: t("clientDetails.reminderText", { ns: "adminui3" }),
        title: t("clientDetails.reminderTitle", { ns: "adminui3" })
        // scheduledAt: new Date(), // facultatif, par défaut: maintenant
        // extraPayload: { source: "clients_list" } // si tu veux enrichir
      });
  
      if (!res.ok) {
        console.error("[notifications_plan.insert] error:", res.error);
        toast({
          title: t("clientDetails.reminderErrorTitle", { ns: "adminui3" }),
          description: t("clientDetails.notificationsError", { ns: "adminui3" }),
          variant: "destructive",
        });
        return;
      }
  
      toast({
        title: t("clientDetails.reminderQueued", { ns: "adminui3" }),
        description: t("clientDetails.reminderCreated", { ns: "adminui3" }),
      });
    })();
  };

  // L'erreur de chargement est signalée dans un effet : émettre le toast
  // pendant le rendu le ré-affichait à chaque passage.
  useEffect(() => {
    if (!client && clientsStore.error) {
      toast({ description: clientsStore.error, title: t("clientDetails.reminderErrorTitle", { ns: "adminui3" }) });
    }
  }, [client, clientsStore.error, toast]);

  // Get the client and check if subscription is expired
  
  // Update client status if it's expired
  useEffect(() => {
    if (client && isSubscriptionExpired(client.expiryDate ?? new Date()) && client.status !== "Expired") {
      console.log("Subscription is expired, updating status...");
    }
    
    // Find active subscription when client data is available
    if (client && client.subscriptionHistory) {
      const active = client.subscriptionHistory.find(
        sub => sub.status === 'Active'
      );
      setCurrentSubscription(active);
    }
  }, [client]);
  
  if (clientsStore.isLoading) {
    return (
      <div className="min-h-screen flex flex-col w-full">
        <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
          <div className="flex items-center space-x-2 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clients')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common:back")}
            </Button>
          </div>
          <div className="text-center py-10">
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }
  
  if (!client && clientsStore.error) {
    return (
      <div className="min-h-screen flex flex-col w-full">
        <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
          <div className="flex flex-col space-y-6">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clients')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("common:back")}
              </Button>
            </div>
            <div className="text-center py-10">
              <h2 className="text-2xl font-bold">{t("clients:notFound")}</h2>
              <p className="text-muted-foreground mt-2">
                {clientsStore.error || t("clients:notFoundDescription")}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  if (!client) {
    return (
      <div className="min-h-screen flex flex-col w-full">
        <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
          <div className="flex flex-col space-y-6">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clients')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("common:back")}
              </Button>
            </div>
            <div className="text-center py-10">
              <h2 className="text-2xl font-bold">{t("clients:notFound")}</h2>
              <p className="text-muted-foreground mt-2">
                {t("clients:notFoundDescription")}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }
  // Check if the subscription is expired
  const isExpired = isSubscriptionExpired(client.expiryDate ?? new Date());
  const isPaused = client.status === "Paused";
  return (
    <div className="min-h-screen flex flex-col w-full">
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full animate-fade-in">
        <div className="flex flex-col space-y-6">
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clients')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common:back")}
            </Button>
          </div>
          
          <div className="flex flex-col md:flex-row gap-6">
            <ClientInfoCard 
              client={{
                ...client,
                status: isExpired && client.status !== "Blocked" && client.status !== "Paused" ? "Expired" : client.status
              }}
              onRenewSubscription={() => setIsEditSubscriptionOpen(true)}
              onBlockClient={() => setIsBlockClientOpen(true)}
              onSendReminder={sendReminder}
            />
            
            <div className="w-full">
              <ClientDetailsTabs 
                client={client} 
                isLoading={clientsStore.isLoading}
                paymentsBySubscription={paymentsBySub}
                onUpdate={() => clientsStore.fetchClientById(id!)}
              />
              
              <div className="mt-4 flex flex-wrap gap-4">
                <Button variant="outline" 
                disabled={isPaused}
                onClick={() => setIsExtendSubscriptionOpen(true)}>
                  {t("clients:extendSubscription")}
                </Button>
                <Button 
                  variant={isPaused ? "default" : "outline"} 
                  className={isPaused ? "bg-amber-500 hover:bg-amber-600" : ""}
                  onClick={() => setIsPauseSubscriptionOpen(true)}
                >
                  {isPaused ? 
                    t("resumeSubscription.title", { ns: "subscriptions" }) : 
                    t("pauseSubscription.title", { ns: "subscriptions" })}
                </Button>
              
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <ClientSubscriptionDialog
        open={isEditSubscriptionOpen}
        onOpenChange={setIsEditSubscriptionOpen}
        client={client}
      />
      
      <ExtendSubscriptionDialog
        open={isExtendSubscriptionOpen}
        onOpenChange={setIsExtendSubscriptionOpen}
        client={client}
        currentSubscription={currentSubscription}
      />
      
      <PauseSubscriptionDialog
        open={isPauseSubscriptionOpen}
        onOpenChange={setIsPauseSubscriptionOpen}
        client={client}
      />
      
      <BlockClientDialog
        open={isBlockClientOpen}
        onOpenChange={setIsBlockClientOpen}
        client={client}
        isBlocked={client.status === "Blocked"}
        onBlockedChange={() => id && clientsStore.fetchClientById(id)}
      />
    </div>
  );
};

export default ClientDetails;
